import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import Page from "components/page/Page";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Select } from "components/select/index";
import { ICategories, IDepartments, IProduct, TProductVendor } from "redux/reducers/models/product";
import axios, { CancelToken } from "axios";
import { GetVendor, PostVendor } from "api/rpc/2024-04/facilityAdmin/product/vendor";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import { capitalize, errorMessage, valueToString } from "helpers/Helpers";
import ReactDOM from "react-dom";
import validator from "validator";
import useModal from "hooks/modals/useModal";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import { useHistory, useParams } from "react-router";
import { isEqualWith, isNull } from "lodash";
import AddFolderProductsModal from "components/folders/AddFolderProductsModal";
import { IProductPickerState, normalizeLoadedVariants, variantsFromSelectedProducts } from "../../settings/teesheets/pricingSheets/pricingSheetApplications";
import { defaultBrowseFilter, IBrowseFilter } from "components/folders/productBrowseFilters";
import { appendUniqueProducts, buildSelectAllProductBatch, fetchFolderProductSearchPage, IFolderProductSearchContext } from "components/folders/folderProductSearch";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { GetDepartments } from "api/rpc/2024-04/facilityAdmin/facility/department";
import { faEllipsisVertical } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import Popup from "components/popup/Popup";
import { Badge } from "components/badge/Badge";
import TextField from "components/form/textField/TextField";
import useCurrencyFacility from "hooks/useCurrency/useCurrencyFacility";
import Callout from "components/callout/Callout";
import PurchaseOrderPageSkeleton from "../purchaseOrders/PurchaseOrderPageSkeleton";
import moment from "moment";
import { DeleteCreditMemo, DeleteCreditMemoItem, GetCreditMemo, GetCreditMemoDownload, PostCreditMemoItems, PutCreditMemo, PutCreditMemoFinalize, PutCreditMemoItems } from "api/rpc/2024-04/facilityAdmin/order/creditMemo";
import { ICreditMemoItem } from "redux/reducers/models/creditMemo";
import { IPurchaseOrder } from "redux/reducers/models/purchaseOrder";

interface IState {
    id: number;
    facility_name: string;
    facility_address_line: string;
    facility_city: string;
    facility_province: string;
    facility_postal: string;
    facility_country: string;

    // Vendor
    vendor_id: number;
    vendor_name: string;
    vendor_address_line: string;
    vendor_city: string;
    vendor_province: string;
    vendor_postal: string;
    vendor_country: string;
    vendor_email: string;
    vendor_phone: string;
    vendor_contact_name: string;
    credit_memo_number: string;
    status: string;
    inventory_effect: "adjust" | "unsellable" | "none" | null;
    source: "receiving_shortfall" | null;
    purchase_order_id: number | null;
    purchase_order: IPurchaseOrder | null;
    items?: Array<ICreditMemoItem>;
    notes: string;
    finalized_at: string;
}

const ITEMS_SELECT_ALL_LIMIT = 200;

function inventoryEffectLabel(effect: string, t: (key: string) => string) {
    switch (effect) {
        case "adjust":
            return t("Inventory adjusted");
        case "unsellable":
            return t("Returned, not sellable");
        case "none":
            return t("No inventory change");
        default:
            return "";
    }
}

type TBrowseFilterState = {
    search: string;
    browseFilter: IBrowseFilter;
};

type TBrowseDepartment = IDepartments & { cateogries?: ICategories[] };

export default function CreditMemoEdit() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { facilityStore } = useAppSelector(store => store);
    const { getCurrencySymbol } = useCurrencyFacility();
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const [vendorSearch, setVendorSearch] = useState<string>("");
    const [vendors, setVendors] = useState<Array<TProductVendor>>([]);
    const [originalVendor, setOriginalVendor] = useState<TProductVendor>(null);
    const [selectedVendor, setSelectedVendor] = useState<TProductVendor>(null);

    const { state: newVendorModal, updateModal: updateNewVendorModal, closeModal: closeNewVendorModal } = useModal<{ title: string; address_line: string; city: string; province: string; country: string; postal_code: string; email: string; phone: string; contact_name: "" }>({
        title: "",
        address_line: "",
        city: "",
        province: "",
        country: "",
        postal_code: "",
        email: "",
        phone: "",
        contact_name: "",
    });

    const { state: editVendorModal, updateModal: updateEditVendorModal, closeModal: closeEditVendorModal } = useModal<{ vendor_name: string; vendor_address_line: string; vendor_city: string; vendor_province: string; vendor_country: string; vendor_postal: string; vendor_email: string; vendor_phone: string; vendor_contact_name: string }>({
        vendor_name: "",
        vendor_address_line: "",
        vendor_city: "",
        vendor_province: "",
        vendor_country: "",
        vendor_postal: "",
        vendor_email: "",
        vendor_phone: "",
        vendor_contact_name: "",
    });

    const { state: editFacilityModal, updateModal: updateFacilityModal, closeModal: closeFacilityModal } = useModal<{ facility_name: string; facility_address_line: string; facility_city: string; facility_province: string; facility_country: string; facility_postal: string }>({
        facility_name: "",
        facility_address_line: "",
        facility_city: "",
        facility_province: "",
        facility_country: "",
        facility_postal: "",
    });

    const { state: editNotesModal, updateModal: updateNotesModal, closeModal: closeNotesModal } = useModal<{ notes: string }>({
        notes: "",
    });

    const { state: removeItemModal, updateModal: updateRemoveItemModal, closeModal: closeRemoveItemModal } = useModal<{ itemId: number }>({
        itemId: null,
    });

    const { state: finalizeModal, updateModal: updateFinalizeModal, closeModal: closeFinalizeModal } = useModal();
    const [inventoryEffect, setInventoryEffect] = useState<"adjust" | "unsellable" | "none">("none");

    const { state: deleteModal, updateModal: updateDeleteModal, closeModal: closeDeleteModal } = useModal();

    const [productState, setProductState] = useState<IProductPickerState>({
        addProductVisibility: false,
        selectedProducts: [],
        addedVariants: [],
        productSearchResults: undefined,
        productSearchTotalCount: undefined,
        productSearchPending: false,
        productSearchLoadingMore: false,
        productSearchSelectAllPending: false,
    });

    const [browseFilterState, setBrowseFilterState] = useState<TBrowseFilterState>({
        search: "",
        browseFilter: defaultBrowseFilter,
    });

    const [browseDepartments, setBrowseDepartments] = useState<TBrowseDepartment[]>([]);
    const [browseMetadataLoading, setBrowseMetadataLoading] = useState(false);
    const [uncategorizedCount, setUncategorizedCount] = useState(0);
    const browseFilterRef = useRef(browseFilterState.browseFilter);
    const prevAddProductsModalOpen = useRef(false);
    const productSearchCancelTokenRef = useRef<ReturnType<typeof axios.CancelToken.source> | null>(null);
    const [vendorFilterEnabled, setVendorFilterEnabled] = useState(true);

    const { Option } = Select;
    const [state, setState] = useState<IState>({
        id: null,
        // Billing
        facility_name: "",
        facility_address_line: "",
        facility_city: "",
        facility_province: "",
        facility_postal: "",
        facility_country: "",

        // Vendor
        vendor_id: null,
        vendor_name: "",
        vendor_address_line: "",
        vendor_city: "",
        vendor_province: "",
        vendor_postal: "",
        vendor_country: "",
        vendor_email: "",
        vendor_phone: "",
        vendor_contact_name: "",
        credit_memo_number: "",
        status: "",
        inventory_effect: null,
        source: null,
        purchase_order_id: null,
        purchase_order: null,
        items: [],
        notes: "",
        finalized_at: "",
    });

    const [beforeState, setBeforeState] = useState<IState>(undefined);

    const excludedVariantIds = useMemo(
        () => productState.addedVariants.map(variant => variant.id).filter((id): id is number => id != null),
        [productState.addedVariants],
    );

    const excludedVariantIdSet = useMemo(() => new Set(excludedVariantIds), [excludedVariantIds]);


    useEffect(() => {
        const source = axios.CancelToken.source();
        void getCreditMemo(source.token);
        return () => source.cancel();
    }, []);

    useEffect(() => {
        const source = axios.CancelToken.source();

        let timeoutId: NodeJS.Timeout = null;
        timeoutId = global.setTimeout(() => {
            void searchVendors(source.token, vendorSearch);
        }, 500);

        return () => {
            clearTimeout(timeoutId);
            setVendors([]);
            source.cancel();
        };
    }, [vendorSearch]);

    useEffect(() => {
        if (!productState.addProductVisibility) {
            productSearchCancelTokenRef.current?.cancel();
            setProductState(prevState => ({
                ...prevState,
                productSearchPending: false,
                productSearchLoadingMore: false,
            }));
            prevAddProductsModalOpen.current = false;
            return;
        }

        const justOpened = !prevAddProductsModalOpen.current;
        prevAddProductsModalOpen.current = true;

        const browseChanged =
            browseFilterRef.current.type !== browseFilterState.browseFilter.type ||
            browseFilterRef.current.id !== browseFilterState.browseFilter.id;
        browseFilterRef.current = browseFilterState.browseFilter;
        const delay = browseChanged ? 0 : 1000;

        let mounted = true;
        productSearchCancelTokenRef.current?.cancel();
        const cancelSource = axios.CancelToken.source();
        productSearchCancelTokenRef.current = cancelSource;

        setProductState(prevState => {
            const hasLoadedResults = (prevState.productSearchResults?.length ?? 0) > 0;

            return {
                ...prevState,
                productSearchPending: true,
                productSearchLoadingMore: false,
                ...(hasLoadedResults ? {} : { productSearchResults: undefined, productSearchTotalCount: undefined }),
            };
        });

        const runFetch = async () => {
            try {
                const { products, totalCount } = await fetchFolderProductSearchPage(
                    GetProduct,
                    getProductSearchContext(),
                    0,
                    true,
                    cancelSource.token,
                    () => dispatch(showError(t("Failed to get products"))),
                );
                if (mounted) {
                    setProductState(prevState => ({
                        ...prevState,
                        productSearchResults: products,
                        productSearchTotalCount: totalCount,
                        productSearchPending: false,
                        productSearchLoadingMore: false,
                    }));
                }
            } catch (error) {
                if (axios.isCancel(error)) {
                    return;
                }
                if (mounted) {
                    setProductState(prevState => ({
                        ...prevState,
                        productSearchResults: [],
                        productSearchTotalCount: 0,
                        productSearchPending: false,
                        productSearchLoadingMore: false,
                    }));
                }
            }
        };

        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        if (justOpened) {
            void runFetch();
        } else {
            timeoutId = global.setTimeout(() => {
                void runFetch();
            }, delay);
        }

        return () => {
            mounted = false;
            cancelSource.cancel();
            if (timeoutId !== undefined) {
                clearTimeout(timeoutId);
            }
        };
    }, [browseFilterState.search, browseFilterState.browseFilter, productState.addProductVisibility, vendorFilterEnabled, state?.vendor_id]);

    async function getCreditMemo(cancelToken?: CancelToken) {
        if (state?.id !== undefined) {
            ReactDOM.unstable_batchedUpdates(() => {
                setState(prev => ({ ...prev, id: undefined }));
                setBeforeState(() => ({ ...state, id: undefined }));
            });
        }
        const res = await GetCreditMemo({ id: Number(id), extended: true }, false, cancelToken);
        if (res?.status !== StatusCode.OK || res?.data?.length === 0) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }

            dispatch(showError(errorMessage(res, t("Error getting credit memo"))));
            return;
        }
        if (res?.data[0]?.vendor_id) {
            const vendorRes = await GetVendor({ id: res?.data[0]?.vendor_id }, false, cancelToken);
            if (vendorRes?.status === StatusCode?.OK && vendorRes?.data?.length > 0) {
                ReactDOM.unstable_batchedUpdates(() => {
                    setSelectedVendor(vendorRes?.data[0]);
                    setVendorSearch(vendorRes?.data[0]?.title ?? "");
                    setOriginalVendor(vendorRes?.data[0]);
                });
            }
        }
        const data = res.data[0];
        const purchaseOrder = data?.purchase_order ?? data?.purchaseOrder ?? null;

        const variants = (data?.items ?? []).map(item => item?.variant).filter((variant): variant is NonNullable<typeof variant> => variant != null);
        const loadedVariants = normalizeLoadedVariants(variants);

        ReactDOM.unstable_batchedUpdates(() => {
            setState({ ...data, notes: data?.notes ?? "", purchase_order: purchaseOrder });
            setBeforeState({ ...data, purchase_order: purchaseOrder });
            setProductState(prev => ({ ...prev, addedVariants: loadedVariants }));
        });

    }

    async function searchVendors(cancelToken: CancelToken, searchQuery: string) {
        if (vendors) {
            setVendors(undefined);
        }
        const vendorRes = await GetVendor({ search: searchQuery }, false, cancelToken);
        if (vendorRes?.status !== StatusCode.OK) {
            setVendors([]);
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(errorMessage(vendorRes, t("Error getting vendors"))));
            return;
        }
        setVendors(vendorRes?.data);
    }

    function onVendorEditInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        const { id, value } = e.target;
        updateEditVendorModal({ [id]: value });
    }

    function onFacilityInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        const { id, value } = e.target;
        updateFacilityModal({ [id]: value });
    }

    function vendorModalInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        const { id, value } = e.target;
        updateNewVendorModal({ [id]: value });
    }

    async function saveNewVendor() {
        const params = {
            ...newVendorModal,
        };

        if (newVendorModal?.email && !validator?.isEmail(newVendorModal?.email)) {
            dispatch(showError(t("The email address format is invalid")));
            return;
        }

        const res = await PostVendor(params, true);
        if (res.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error creating vendor"))));
            return;
        }

        dispatch(showSuccess(t("Successfully created vendor")));
        const vendor = res?.data;
        ReactDOM.unstable_batchedUpdates(() => {
            setVendorSearch(vendor?.title);
            updateEditVendorModal({
                isOpen: true,
                vendor_name: vendor.title,
                vendor_address_line: vendor.address_line,
                vendor_city: vendor.city,
                vendor_province: vendor.province,
                vendor_postal: vendor.postal_code,
                vendor_country: vendor.country,
                vendor_contact_name: vendor.contact_name,
                vendor_email: vendor.email,
                vendor_phone: vendor.phone,
            });
            setSelectedVendor(vendor);
        });
        void closeNewVendorModal();
    }

    async function updateVendor() {
        if (editVendorModal?.vendor_email && !validator.isEmail(editVendorModal?.vendor_email)) {
            dispatch(showError(t("The email address format is invalid")));
            return;
        }
        const res = await PutCreditMemo({
            id: Number(id),
            vendor_name: editVendorModal.vendor_name,
            vendor_address_line: editVendorModal.vendor_address_line,
            vendor_city: editVendorModal.vendor_city,
            vendor_province: editVendorModal.vendor_province,
            vendor_postal: editVendorModal.vendor_postal,
            vendor_country: editVendorModal.vendor_country,
            vendor_contact_name: editVendorModal.vendor_contact_name || null,
            vendor_email: editVendorModal.vendor_email || null,
            vendor_phone: editVendorModal.vendor_phone || null,
            vendor_id: selectedVendor?.id ?? null,
        }, true);

        if (res?.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error updating credit memo"))));
            return;
        }
        void closeEditVendorModal();
        dispatch(showSuccess(t("Successfully updated credit memo")));
        await getCreditMemo();
    }
    async function updateFacility() {
        const res = await PutCreditMemo({
            id: Number(id),
            facility_name: editFacilityModal.facility_name,
            facility_address_line: editFacilityModal.facility_address_line,
            facility_city: editFacilityModal.facility_city,
            facility_province: editFacilityModal.facility_province,
            facility_postal: editFacilityModal.facility_postal,
            facility_country: editFacilityModal.facility_country,
        }, true);

        if (res?.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error updating credit memo facility"))));
            return;
        }
        void closeFacilityModal();
        dispatch(showSuccess(t("Successfully updated credit memo")));
        await getCreditMemo();
    }

    function handleAddProductsBrowseSearchChange(value: string) {
        setBrowseFilterState(prevState => ({ ...prevState, search: value }));
        setProductState(prev => ({ ...prev, productSearchPending: true }));
    }

    function handleAddProductsBrowseFilterChange(browseFilter: IBrowseFilter) {
        setBrowseFilterState(prevState => ({ ...prevState, browseFilter }));
        setProductState(prev => ({ ...prev, productSearchPending: true }));
    }

    async function loadMoreProducts() {
        let shouldLoad = false;
        let offset = 0;

        setProductState(prev => {
            if (
                prev.productSearchLoadingMore ||
                prev.productSearchPending ||
                !prev.productSearchResults ||
                prev.productSearchTotalCount == null ||
                prev.productSearchResults.length >= prev.productSearchTotalCount
            ) {
                return prev;
            }

            shouldLoad = true;
            offset = prev.productSearchResults.length;
            return { ...prev, productSearchLoadingMore: true };
        });

        if (!shouldLoad) {
            return;
        }

        try {
            const { products } = await fetchFolderProductSearchPage(
                GetProduct,
                getProductSearchContext(),
                offset,
                false,
                undefined,
                () => dispatch(showError(t("Failed to get products"))),
            );
            setProductState(prev => ({
                ...prev,
                productSearchResults: appendUniqueProducts(prev.productSearchResults ?? [], products),
                productSearchLoadingMore: false,
            }));
        } catch {
            setProductState(prev => ({ ...prev, productSearchLoadingMore: false }));
        }
    }

    function getProductSearchContext(): IFolderProductSearchContext {
        return {
            search: browseFilterState.search,
            browseFilter: browseFilterState.browseFilter,
            variants: true,
            vendor_id: vendorFilterEnabled ? state?.vendor_id : undefined,
        };
    }

    async function selectAllMatchingProducts() {
        const loaded = productState.productSearchResults ?? [];
        const totalCount = productState.productSearchTotalCount ?? loaded.length;
        const fullyAddedIds = new Set(
            loaded.filter(product => withSelectableVariants(product) == null).map(product => product.id),
        );
        const fullySelectedIds = new Set(
            productState.selectedProducts
                .filter(selected => {
                    const source = loaded.find(product => product.id === selected.id) ?? selected;
                    const available = withSelectableVariants(source);
                    if (available == null) {
                        return true;
                    }

                    const selectedVariantIds = new Set((selected.variants ?? []).map(variant => variant.id));
                    return available.variants.every(variant => selectedVariantIds.has(variant.id));
                })
                .map(product => product.id),
        );
        const excludeIds = new Set<number>([...fullyAddedIds, ...fullySelectedIds]);
        const remaining = Math.max(0, ITEMS_SELECT_ALL_LIMIT - productState.selectedProducts.length);
        const needsRemoteFetch = remaining > 0 && loaded.length < totalCount;

        if (needsRemoteFetch) {
            setProductState(prev => ({ ...prev, productSearchSelectAllPending: true }));
        }

        try {
            const toAdd = await buildSelectAllProductBatch(
                GetProduct,
                getProductSearchContext(),
                loaded,
                totalCount,
                excludeIds,
                remaining,
                () => dispatch(showError(t("Failed to get products"))),
            );

            const selectable = toAdd
                .map(product => withSelectableVariants(product))
                .filter((product): product is IProduct => product != null);

            setProductState(prev => {
                if (selectable.length === 0) {
                    return { ...prev, productSearchSelectAllPending: false };
                }

                return {
                    ...prev,
                    selectedProducts: mergeSelectedProducts(prev.selectedProducts, selectable),
                    productSearchSelectAllPending: false,
                };
            });
        } catch {
            setProductState(prev => ({ ...prev, productSearchSelectAllPending: false }));
        }
    }

    function mergeSelectedProducts(existing: IProduct[], incoming: IProduct[]) {
        const next = [...existing];

        incoming.forEach(product => {
            const index = next.findIndex(selected => selected.id === product.id);
            if (index === -1) {
                next.push(product);
                return;
            }

            const existingVariantIds = new Set((next[index].variants ?? []).map(variant => variant.id));
            const mergedVariants = [...(next[index].variants ?? [])];
            (product.variants ?? []).forEach(variant => {
                if (!existingVariantIds.has(variant.id)) {
                    mergedVariants.push(variant);
                    existingVariantIds.add(variant.id);
                }
            });
            next[index] = { ...next[index], variants: mergedVariants, variant_count: mergedVariants.length };
        });

        return next;
    }

    function withSelectableVariants(product: IProduct): IProduct | null {
        const variants = (product.variants ?? []).filter(variant => !excludedVariantIdSet.has(variant.id));
        if (variants.length === 0) {
            return null;
        }

        return {
            ...product,
            variants,
            variant_count: variants.length,
        };
    }

    function handleSelectProductToAdd(productOrProducts: IProduct | IProduct[]) {
        const products = Array.isArray(productOrProducts) ? productOrProducts : [productOrProducts];
        const selectable = products
            .map(product => withSelectableVariants(product))
            .filter((product): product is IProduct => product != null);

        if (selectable.length === 0) {
            return;
        }

        setProductState(prev => ({
            ...prev,
            selectedProducts: mergeSelectedProducts(prev.selectedProducts, selectable),
        }));
    }

    function handleUnselectProductToAdd(productOrProducts: IProduct | IProduct[]) {
        const products = Array.isArray(productOrProducts) ? productOrProducts : [productOrProducts];

        setProductState(prev => {
            let next = [...prev.selectedProducts];

            products.forEach(incoming => {
                const index = next.findIndex(product => product.id === incoming.id);
                if (index === -1) {
                    return;
                }

                const removeIds = new Set((incoming.variants ?? []).map(variant => variant.id));
                if (removeIds.size === 0) {
                    next = next.filter(product => product.id !== incoming.id);
                    return;
                }

                const remaining = (next[index].variants ?? []).filter(variant => !removeIds.has(variant.id));
                if (remaining.length === 0) {
                    next = next.filter(product => product.id !== incoming.id);
                } else {
                    next[index] = { ...next[index], variants: remaining, variant_count: remaining.length };
                }
            });

            return { ...prev, selectedProducts: next };
        });
    }

    async function loadBrowseMetadata() {
        const showLoading = browseDepartments.length === 0;

        if (showLoading) {
            setBrowseMetadataLoading(true);
        }

        try {
            const [departmentRes, countRes] = await Promise.all([
                GetDepartments({ extended: true }, false),
                GetProduct(
                    {
                        get_count: true,
                        limit: 1,
                        filters: [{ label: "uncategorized", value: [1] }],
                        vendor_id: vendorFilterEnabled ? state?.vendor_id : undefined,
                    },
                    false,
                ),
            ]);

            if (departmentRes.status === StatusCode.OK) {
                setBrowseDepartments((departmentRes.data ?? []).filter(department => department.type === "department"));
            }

            if (countRes.status === StatusCode.OK && countRes.data && !Array.isArray(countRes.data)) {
                const countData = countRes.data as { count?: number };
                setUncategorizedCount(countData.count ?? 0);
            }
        } finally {
            if (showLoading) {
                setBrowseMetadataLoading(false);
            }
        }
    }

    function handleBrowseProductsVisibility() {
        const opening = !productState.addProductVisibility;

        setProductState(prevState => ({
            ...prevState,
            addProductVisibility: opening,
            selectedProducts: [],
            ...(opening
                ? {
                    productSearchPending: true,
                    productSearchResults: undefined,
                    productSearchTotalCount: undefined,
                }
                : {}),
        }));

        if (opening) {
            setBrowseFilterState({ search: "", browseFilter: defaultBrowseFilter });
            void loadBrowseMetadata();
        } else {
            setBrowseFilterState({ search: "", browseFilter: defaultBrowseFilter });
        }
    }

    async function handleAddProducts(products: IProduct[]) {
        const incomingVariants = variantsFromSelectedProducts(products);

        if (incomingVariants.length === 0) {
            dispatch(showError(t("A product must be selected")));
            return;
        }

        const res = await PostCreditMemoItems({ credit_memo_id: Number(id), variants: incomingVariants?.map(variant => ({ variant_id: variant?.id, quantity: 1, unit_cost: variant?.cost != null ? variant?.cost : 0 })) }, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error adding items to credit memo"))));
            return;
        }
        dispatch(showSuccess(t("Successfully added items to credit memo")));
        setProductState(prevState => ({
            ...prevState,
            selectedProducts: [],
            addProductVisibility: false,
        }));
        setBrowseFilterState({ search: "", browseFilter: defaultBrowseFilter });

        await getCreditMemo();
    }

    function handleItemInputChange(e: React.ChangeEvent<HTMLInputElement>, index: number) {
        const { id, value } = e.target;
        const updatedItems = [...state.items];
        updatedItems[index] = { ...updatedItems[index], [id]: value };
        setState(prev => ({ ...prev, items: updatedItems }));
    }

    function unsavedChangesExist() {
        if (beforeState === undefined) {
            if (state?.id) {
                setBeforeState({ ...state });
            }
            return false;
        }

        return !isEqualWith(beforeState, state, (originalValue, newValue) => {
            if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
                return true;
            }
        });
    }

    function cancelUnsavedChanges() {
        setState({ ...beforeState });
    }

    async function updateCreditMemoItems() {
        const updatedItems = state?.items?.map(item => ({ id: item.id, quantity: item.quantity, unit_cost: item?.unit_cost, notes: item?.notes }));
        const res = await PutCreditMemoItems({ credit_memo_id: Number(id), items: updatedItems }, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error updating items in credit memo"))));
            return;
        }
        dispatch(showSuccess(t("Successfully updated items in credit memo")));
        await getCreditMemo();
    }

    async function deleteCreditMemoItem() {
        const res = await DeleteCreditMemoItem({ id: removeItemModal?.itemId }, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error removing item in credit memo"))));
            return;
        }
        void closeRemoveItemModal();
        dispatch(showSuccess(t("Successfully removed item in credit memo")));
        await getCreditMemo();
    }

    async function updateNotes() {
        const res = await PutCreditMemo({
            id: Number(id),
            notes: editNotesModal?.notes,
        }, true);

        if (res?.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error updating credit memo notes"))));
            return;
        }

        void closeNotesModal();
        dispatch(showSuccess(t("Successfully updated credit memo notes")));
        await getCreditMemo();
    }

    async function handleFinalize() {
        const res = await PutCreditMemoFinalize({
            id: Number(id),
            inventory_effect: state?.source === "receiving_shortfall" ? "none" : inventoryEffect,
        }, true);

        if (res?.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error finalzing credit memo"))));
            return;
        }

        void closeFinalizeModal();
        dispatch(showSuccess(t("Successfully finalized credit memo")));
        await getCreditMemo();
    }

    async function handleDownload() {
        const res = await GetCreditMemoDownload({
            id: Number(id),
        }, true);

        if (res?.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error downloading credit memo"))));
            return;
        }

        window.open(String(res.data));
    }

    async function handleDelete() {
        const res = await DeleteCreditMemo({
            id: Number(id),
        }, true);

        if (res?.status !== StatusCode.OK) {
            dispatch(showError(errorMessage(res, t("Error deleting credit memo"))));
            return;
        }

        void closeDeleteModal();
        dispatch(showSuccess(t("Successfully deleted credit memo")));
        history.push("/admin/settings/credit-memos");
    }

    return (
        <>

            <Page
                title={`${t("Credit Memo #")}: ${state?.credit_memo_number}`}
                splitLayout
                breadcrumbs={[{
                    prefix: true,
                    label: t("Back to credit memos"),
                    url: "/admin/settings/credit-memos",
                }]}
                subtitle={state?.status !== "draft" && state?.finalized_at ? `${t("Finalized on")} ${moment.utc(state?.finalized_at, "YYYY-MM-DD HH:mm:ss")?.local()?.format("LLL")}${state?.inventory_effect ? ` · ${inventoryEffectLabel(state.inventory_effect, t)}` : ""}` : ""}
                primaryAction={state?.status === "draft" ? { content: t("Save"), action: () => updateCreditMemoItems(), disabled: state?.id === undefined } : undefined}
                titleMetadata={<Badge type={state?.status === "draft" ? "gray" : "success"} size="medium">{capitalize(state?.status)}</Badge>}
                notificationBarProps={{
                    isVisible: unsavedChangesExist(),
                    onAction: () => updateCreditMemoItems(),
                    onCancel: cancelUnsavedChanges,
                }}
                multipleActionDropdownAction={{
                    label: t("Options"),
                    dropdownProps: {
                        alignment: "right",
                        options: [
                            {
                                type: "handler",
                                label: t("Download"),
                                icon: "download",
                                handler: () => handleDownload(),
                            },
                            {
                                type: "handler",
                                label: t("Finalize"),
                                icon: "check",
                                handler: () => {
                                    setInventoryEffect("none");
                                    updateFinalizeModal({ isOpen: true });
                                },
                                disabled: state?.status !== "draft",
                            },
                            {
                                type: "handler",
                                label: t("Delete"),
                                icon: "trash-can",
                                handler: () => updateDeleteModal({ isOpen: true }),
                                disabled: state?.status !== "draft",
                            },
                        ],
                    },
                }}
            >
                {state?.id === undefined ? (
                    <PurchaseOrderPageSkeleton creditMemo/>
                ) : (
                    <>
                        <Page.Section twoThirds>
                            {state?.purchase_order ? (
                                <Card
                                    title={t("Purchase Order")}
                                    titleActions={[{
                                        content: t("View"),
                                        action: () => history.push(`/admin/settings/purchase-orders/${state.purchase_order.id}`),
                                    }]}
                                >
                                    <Card.Section>
                                        <div className="flex justify-between items-start gap-4">
                                            <div>
                                                <p className="text-lg text-semibold">{state.purchase_order.po_number}</p>
                                                {state.purchase_order.vendor_name ? <p className="text-md">{state.purchase_order.vendor_name}</p> : null}
                                                <p className="text-sm text-gray-500">
                                                    {state.purchase_order.ordered_at
                                                        ? `${t("Ordered")} ${moment.utc(state.purchase_order.ordered_at, "YYYY-MM-DD HH:mm:ss").local().format("LL")}`
                                                        : `${t("Created")} ${moment.utc(state.purchase_order.created_at, "YYYY-MM-DD HH:mm:ss").local().format("LL")}`}
                                                </p>
                                                {state.purchase_order.notes ? <p className="text-sm text-gray-500 mt-2">{state.purchase_order.notes}</p> : null}
                                            </div>
                                            <Badge type={state.purchase_order.status === "closed" ? "success" : state.purchase_order.status === "cancelled" ? "error" : "gray"} size="medium">
                                                {valueToString(state.purchase_order.status)}
                                            </Badge>
                                        </div>
                                    </Card.Section>
                                </Card>
                            ) : null}
                            <Card
                                title={t("Items")}
                                titleActions={[
                                    {
                                        content: t("Add Items"),
                                        action: () => handleBrowseProductsVisibility(),
                                        disabled: state?.status !== "draft",
                                    },
                                ]}>
                                <Card.Section>
                                    {state?.items?.map((item, index) => {
                                        return (
                                            <Card.SubSection key={index}>
                                                <div className="flex justify-between items-center">
                                                    <div>
                                                        <p className="text-lg text-semibold">{item.product_name}</p>
                                                        {item.product_name !== item.variant_name ? (
                                                            <p className="text-sm text-gray-500">{item.variant_name}</p>
                                                        ) : null}
                                                        {item?.variant?.sku ? <p className="text-sm text-gray-500">{t("SKU")} - {item?.variant?.sku}</p> : null}
                                                        {item?.variant?.barcode ? <p className="text-sm text-gray-500">{t("Barcode")} - {item?.variant?.barcode}</p> : null}
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <Input
                                                            value={item?.quantity}
                                                            label={t("Quantity")}
                                                            id={"quantity"}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleItemInputChange(e, index)}
                                                            type="number"
                                                            min={1}
                                                            placeholder={"1"}
                                                            disabled={state?.status !== "draft"}
                                                        />
                                                        <Input
                                                            value={item?.unit_cost}
                                                            label={t("Unit cost")}
                                                            id={"unit_cost"}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleItemInputChange(e, index)}
                                                            type="number"
                                                            prefix={getCurrencySymbol(facilityStore?.facility?.default_currency_code)}
                                                            min={0}
                                                            disabled={state?.status !== "draft"}
                                                        />
                                                        {state?.status === "draft" ? (
                                                            <div className="mt-3">
                                                                <NavigationDropdownNew
                                                                    showPlainTextLabel
                                                                    rightAlign
                                                                    label={<FontAwesomeIcon icon={faEllipsisVertical} />}
                                                                    sections={[
                                                                        [
                                                                            {
                                                                                label: t("Remove"),
                                                                                icon: "x",
                                                                                onClick: () => updateRemoveItemModal({ itemId: item?.id, isOpen: true }),
                                                                                disabled: state?.status !== "draft",
                                                                            },
                                                                        ],
                                                                    ]}
                                                                />
                                                            </div>
                                                        ) : null}

                                                    </div>
                                                </div>
                                            </Card.SubSection>
                                        );
                                    })}
                                </Card.Section>
                            </Card>
                        </Page.Section>
                        <Page.Section oneThird>
                            <Card>
                                <Card.Section title={t("Vendor")} sectionTitleActions={state?.status === "draft" ? [{
                                    content: t("Edit"), action: () => updateEditVendorModal({
                                        isOpen: true,
                                        vendor_name: state.vendor_name,
                                        vendor_address_line: state.vendor_address_line,
                                        vendor_city: state.vendor_city,
                                        vendor_province: state.vendor_province,
                                        vendor_postal: state.vendor_postal,
                                        vendor_country: state.vendor_country,
                                        vendor_contact_name: state.vendor_contact_name,
                                        vendor_email: state.vendor_email,
                                        vendor_phone: state.vendor_phone,
                                    }),
                                }] : undefined}>
                                    <p className="text-md">{state?.vendor_name}</p>
                                    <p className="text-md">{state?.vendor_address_line}, {state?.vendor_city} {state?.vendor_province} {state?.vendor_country} {state?.vendor_postal}</p>
                                    <p className="text-md">{state?.vendor_email}</p>
                                    <p className="text-md">{state?.vendor_phone}</p>
                                    <p className="text-md">{state?.vendor_contact_name}</p>
                                </Card.Section>
                            </Card>
                            <Card>
                                <Card.Section title={t("Facility")} sectionTitleActions={state?.status === "draft" ? [{
                                    content: t("Edit"), action: () => updateFacilityModal({
                                        isOpen: true,
                                        facility_name: state.facility_name,
                                        facility_address_line: state.facility_address_line,
                                        facility_city: state.facility_city,
                                        facility_province: state.facility_province,
                                        facility_postal: state.facility_postal,
                                        facility_country: state.facility_country,
                                    }),
                                }] : undefined}>
                                    <p className="text-md">{state?.facility_name}</p>
                                    <p className="text-md">{state?.facility_address_line}, {state?.facility_city} {state?.facility_province} {state?.facility_country} {state?.facility_postal}</p>
                                </Card.Section>
                            </Card>
                            <Card>
                                <Card.Section title={t("Notes")} sectionTitleActions={state?.status === "draft" ? [{
                                    content: t("Edit"), action: () => updateNotesModal({ isOpen: true, notes: state?.notes ?? "" }),
                                }] : undefined}>
                                    {state?.notes ? (
                                        <p className="text-md">{state?.notes}</p>
                                    ) : <Callout title={t("No notes")} type="info" content={t("Click edit to add notes to this credit memo")} />}

                                </Card.Section>
                            </Card>
                        </Page.Section>
                    </>
                )}
            </Page>

            {/* EDIT FACILITY */}
            <Portal isMounted={editFacilityModal?.isOpen}>
                <Sheet
                    title={t("Edit facility")}
                    open={editFacilityModal?.isOpen}
                    size="small"
                    stacked
                    onCancel={closeFacilityModal}
                    onOk={updateFacility}
                    okText={t("Update")}
                    closable
                >
                    <FormLayout>
                        <FormLayout.Group>
                            <Input
                                label={t("Facility Name")}
                                value={editFacilityModal?.facility_name}
                                onChange={onFacilityInputChange}
                                id="facility_name"
                                placeholder={t("Facility Name")}
                            />
                        </FormLayout.Group>

                        <FormLayout.Group>
                            <Input
                                label={t("Address Line")}
                                value={editFacilityModal?.facility_address_line}
                                onChange={onFacilityInputChange}
                                id="facility_address_line"
                                placeholder={t("Address Line")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                label={t("City")}
                                value={editFacilityModal?.facility_city}
                                onChange={onFacilityInputChange}
                                id="facility_city"
                                placeholder={t("City")}
                            />
                            <Input
                                label={t("Province")}
                                value={editFacilityModal?.facility_province}
                                onChange={onFacilityInputChange}
                                id="facility_province"
                                placeholder={t("Province")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                label={t("Country")}
                                value={editFacilityModal?.facility_country}
                                onChange={onFacilityInputChange}
                                id="facility_country"
                                placeholder={t("Country")}
                            />
                            <Input
                                label={t("Postal Code")}
                                value={editFacilityModal?.facility_postal}
                                onChange={onFacilityInputChange}
                                id="facility_postal"
                                placeholder={t("Postal Code")}
                            />
                        </FormLayout.Group>
                    </FormLayout>
                </Sheet>
            </Portal>


            {/* EDIT VENDOR */}
            <Portal isMounted={editVendorModal?.isOpen}>
                <Sheet
                    title={t("Edit vendor")}
                    open={editVendorModal?.isOpen}
                    size="small"
                    stacked
                    onCancel={() => {
                        void closeEditVendorModal();
                        setSelectedVendor(originalVendor ? { ...originalVendor } : null);
                        setVendorSearch(originalVendor ? originalVendor?.title : "");
                    }}
                    onOk={updateVendor}
                    okText={t("Update")}
                    closable
                >
                    <FormLayout>
                        <FormLayout.Group>
                            <Select
                                label={t("Vendor")}
                                placeholder={t("Vendor")}
                                showSearch
                                onSearch={(query: string) => {
                                    setVendorSearch(query);
                                    if (!query) {
                                        setSelectedVendor(null);
                                    }
                                }}
                                onChange={(_, vendor: TProductVendor) => {
                                    ReactDOM.unstable_batchedUpdates(() => {
                                        setSelectedVendor(vendor);
                                        setVendorSearch(vendor?.title);
                                        if (vendor) {
                                            updateEditVendorModal({
                                                isOpen: true,
                                                vendor_name: vendor.title ?? "",
                                                vendor_address_line: vendor.address_line ?? "",
                                                vendor_city: vendor.city ?? "",
                                                vendor_province: vendor.province ?? "",
                                                vendor_postal: vendor.postal_code ?? "",
                                                vendor_country: vendor.country ?? "",
                                                vendor_contact_name: vendor.contact_name ?? "",
                                                vendor_email: vendor.email ?? "",
                                                vendor_phone: vendor.phone ?? "",
                                            });
                                        }
                                    });
                                }}
                                allowClear
                                searchValue={vendorSearch}
                                showDropDownOnFocus={true}
                                searching={vendors === undefined}
                                defaultValue={selectedVendor?.id}
                                // usePortal
                                hideDropdown
                            >
                                <div
                                    className="ui-select-dropdown-list-item"
                                    onClick={() => updateNewVendorModal({ isOpen: true, title: capitalize(vendorSearch ?? "") })}
                                >
                                    <p className="text-md">{t("New")}</p>
                                </div>
                                {vendors?.map((vendor, index) => {
                                    return (
                                        <Option key={index} value={vendor.id} extraValues={vendor}>
                                            {vendor?.title}
                                        </Option>
                                    );
                                })}
                            </Select>
                        </FormLayout.Group>
                    </FormLayout>

                    <FormLayout>
                        <FormLayout.Group>
                            <Input
                                label={t("Vendor Name")}
                                value={editVendorModal?.vendor_name}
                                onChange={onVendorEditInputChange}
                                id="vendor_name"
                                placeholder={t("Vendor Name")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                label={t("Address Line")}
                                value={editVendorModal?.vendor_address_line}
                                onChange={onVendorEditInputChange}
                                id="vendor_address_line"
                                placeholder={t("Address Line")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                label={t("City")}
                                value={editVendorModal?.vendor_city}
                                onChange={onVendorEditInputChange}
                                id="vendor_city"
                                placeholder={t("City")}
                            />
                            <Input
                                label={t("Province")}
                                value={editVendorModal?.vendor_province}
                                onChange={onVendorEditInputChange}
                                id="vendor_province"
                                placeholder={t("Province")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                label={t("Country")}
                                value={editVendorModal?.vendor_country}
                                onChange={onVendorEditInputChange}
                                id="vendor_country"
                                placeholder={t("Country")}
                            />
                            <Input
                                label={t("Postal Code")}
                                value={editVendorModal?.vendor_postal}
                                onChange={onVendorEditInputChange}
                                id="vendor_postal"
                                placeholder={t("Postal Code")}
                            />
                        </FormLayout.Group>
                    </FormLayout>

                    <FormLayout>
                        <FormLayout.Group>
                            <Input
                                label={t("Email")}
                                value={editVendorModal?.vendor_email}
                                onChange={onVendorEditInputChange}
                                id="vendor_email"
                                placeholder={t("Email")}
                                type="email"
                                error={editVendorModal?.vendor_email && !validator.isEmail(editVendorModal?.vendor_email)}
                            />
                            <Input
                                label={t("Phone")}
                                value={editVendorModal?.vendor_phone}
                                onChange={onVendorEditInputChange}
                                id="vendor_phone"
                                placeholder={t("Phone")}
                                type="number"
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                label={t("Contact Name")}
                                value={editVendorModal?.vendor_contact_name}
                                onChange={onVendorEditInputChange}
                                id="vendor_contact_name"
                                placeholder={t("Contact Name")}
                            />
                        </FormLayout.Group>
                    </FormLayout>
                </Sheet>
            </Portal>

            <Portal isMounted={editNotesModal?.isOpen}>
                <Sheet
                    title={t("Notes")}
                    open={editNotesModal?.isOpen}
                    size="small"
                    stacked
                    onCancel={closeNotesModal}
                    onOk={updateNotes}
                    okText={t("Save")}
                    closable
                >
                    <TextField rows={5} label={t("Notes")} placeholder={t("Add notes...")} maxLength={255} characterCountBottom onChange={e => updateNotesModal({ notes: e.target.value })} value={editNotesModal?.notes ?? ""} />
                </Sheet>
            </Portal>

            {/* NEW VENDOR */}
            <Portal isMounted={newVendorModal?.isOpen}>
                <Sheet
                    title={t("New vendor")}
                    open={newVendorModal?.isOpen}
                    size="small"
                    stacked
                    onCancel={closeNewVendorModal}
                    onOk={saveNewVendor}
                    okText={t("Create")}
                    closable
                >
                    <FormLayout>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.title}
                                label={t("Name")}
                                id="title"
                                onChange={vendorModalInputChange}
                                placeholder={t("Name")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.address_line}
                                label={t("Address")}
                                id="address_line"
                                onChange={vendorModalInputChange}
                                placeholder={t("Address")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.city}
                                label={t("City")}
                                id="city"
                                onChange={vendorModalInputChange}
                                placeholder={t("City")}
                            />
                            <Input
                                value={newVendorModal.province}
                                label={t("Province")}
                                id="province"
                                onChange={vendorModalInputChange}
                                placeholder={t("Province")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.country}
                                label={t("Country")}
                                id="country"
                                onChange={vendorModalInputChange}
                                placeholder={t("Country")}
                            />
                            <Input
                                value={newVendorModal.postal_code}
                                label={t("Postal Code")}
                                id="postal_code"
                                onChange={vendorModalInputChange}
                                placeholder={t("Postal Code")}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.email}
                                label={t("Email")}
                                id="email"
                                type="email"
                                onChange={vendorModalInputChange}
                                placeholder={t("Email")}
                                error={newVendorModal?.email && !validator.isEmail(newVendorModal?.email)}
                            />
                            <Input
                                value={newVendorModal.phone}
                                label={t("Phone")}
                                id="phone"
                                type="number"
                                onChange={vendorModalInputChange}
                                placeholder={t("Phone")}
                                error={newVendorModal?.phone && !validator?.isNumeric(newVendorModal?.phone)}
                            />
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input
                                value={newVendorModal.contact_name}
                                label={t("Contact Name")}
                                id="contact_name"
                                onChange={vendorModalInputChange}
                                placeholder={t("Contact Name")}
                            />
                        </FormLayout.Group>
                    </FormLayout>
                </Sheet>
            </Portal>

            <Portal isMounted={finalizeModal?.isOpen}>
                <Popup
                    open={finalizeModal?.isOpen}
                    type="warning"
                    aligned="left"
                    okText={t("Finalize")}
                    title={t("Finalize credit memo")}
                    description={
                        state?.source === "receiving_shortfall"
                            ? t("This credit memo will no longer be editable. Inventory was already updated when the purchase order was received, so finalizing will not change stock.")
                            : t("This credit memo will no longer be editable. Choose how inventory should be updated.")
                    }
                    onOk={handleFinalize}
                    onCancel={closeFinalizeModal}
                >
                    {state?.source === "receiving_shortfall" ? null : <div className="flex flex-col gap-3 text-left mt-2">
                        <label className="flex gap-2">
                            <input type="radio" name="credit-memo-inventory-effect" checked={inventoryEffect === "adjust"} onChange={() => setInventoryEffect("adjust")} />
                            <span>
                                <span className="text-semibold">{t("Adjust Inventory")}</span>
                                <p className="text-sm text-gray-500">{t("Reduce inventory for the credit memo quantities.")}</p>
                            </span>
                        </label>
                        <label className="flex gap-2">
                            <input type="radio" name="credit-memo-inventory-effect" checked={inventoryEffect === "unsellable"} onChange={() => setInventoryEffect("unsellable")} />
                            <span>
                                <span className="text-semibold">{t("Return but not sellable")}</span>
                                <p className="text-sm text-gray-500">{t("Reduce inventory. The return is not sellable.")}</p>
                            </span>
                        </label>
                        <label className="flex gap-2">
                            <input type="radio" name="credit-memo-inventory-effect" checked={inventoryEffect === "none"} onChange={() => setInventoryEffect("none")} />
                            <span>
                                <span className="text-semibold">{t("No return")}</span>
                                <p className="text-sm text-gray-500">{t("Do not change inventory.")}</p>
                            </span>
                        </label>
                    </div>}
                </Popup>
            </Portal>

            <Portal isMounted={deleteModal?.isOpen}>
                <Popup open={deleteModal?.isOpen} type="warning" okText={t("Delete")} title={t("Delete Credit Memo")} description={t("Are you sure you want to delete the credit memo? This cannot be undone.")} onOk={handleDelete} onCancel={closeDeleteModal} />
            </Portal>

            <AddFolderProductsModal
                open={productState.addProductVisibility}
                onClose={handleBrowseProductsVisibility}
                onAdd={handleAddProducts}
                search={browseFilterState.search}
                onSearchChange={handleAddProductsBrowseSearchChange}
                browseFilter={browseFilterState.browseFilter}
                onBrowseFilterChange={handleAddProductsBrowseFilterChange}
                departments={browseDepartments}
                departmentsLoading={browseMetadataLoading}
                uncategorizedCount={uncategorizedCount}
                productsToAdd={productState.selectedProducts}
                availableSlotCount={ITEMS_SELECT_ALL_LIMIT}
                totalEmptySlotCount={ITEMS_SELECT_ALL_LIMIT}
                folderProducts={[]}
                productSearchResults={productState.productSearchResults}
                productSearchTotalCount={productState.productSearchTotalCount}
                productSearchPending={productState.productSearchPending}
                productSearchLoadingMore={productState.productSearchLoadingMore}
                productSearchSelectAllPending={productState.productSearchSelectAllPending}
                onLoadMoreProducts={loadMoreProducts}
                onSelectAllMatchingProducts={selectAllMatchingProducts}
                onSelectProduct={handleSelectProductToAdd}
                onUnselectProduct={handleUnselectProductToAdd}
                hideSlots
                enableVariantSelection
                excludedVariantIds={excludedVariantIds}
                title={t("Browse products")}
                supportText={t("Add items to this credit memo")}
                onCreateProduct={() => {
                    void handleBrowseProductsVisibility();
                    history.push("/admin/product/new");
                }}
                onGoToProducts={() => {
                    void handleBrowseProductsVisibility();
                    history.push("/admin/product");
                }}
                vendorId={state?.vendor_id}
                vendorFilterEnabled={vendorFilterEnabled}
                onVendorFilterChange={setVendorFilterEnabled}
            />

            <Portal isMounted={removeItemModal?.isOpen}>
                <Popup type="warning" open={removeItemModal?.isOpen} title={t("Remove Item")} description={t("Are you sure you want to remove this item from the credit memo?")} onCancel={closeRemoveItemModal} onOk={deleteCreditMemoItem} okText={t("Remove")} />
            </Portal>
        </>
    );
}